import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Calendar, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { formatDateTime } from "../../lib/utils-format";

export default function MyListingsPage() {
    const { t, i18n } = useTranslation();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const locale = i18n.language === "fr" ? "fr-FR" : i18n.language === "ht" ? "fr-FR" : "en-US";

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/api/organizer/events");
                setEvents(data || []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div data-testid="my-listings-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="font-heading font-black text-3xl md:text-4xl">{t("organizer.my_listings")}</h1>
                <Link to="/organizer/create-event" data-testid="create-event-btn" className="btn-primary inline-flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    {t("organizer.create_event")}
                </Link>
            </div>

            {loading ? (
                <div className="text-muted-foreground">{t("common.loading")}</div>
            ) : events.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-border p-12 text-center text-muted-foreground">
                    {t("organizer.no_events")}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {events.map((e) => (
                        <div key={e.id} data-testid={`listing-${e.id}`} className="bento-card overflow-hidden">
                            <div className="aspect-square w-full bg-muted relative">
                                {e.featured_image_url ? (
                                    <img src={e.featured_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Calendar className="h-10 w-10 text-muted-foreground" />
                                    </div>
                                )}
                                <span className={`absolute top-3 right-3 badge-pill ${e.status === "live" ? "bg-[hsl(var(--success))] text-white" : "bg-[hsl(var(--warning))] text-white"}`}>
                                    {e.status}
                                </span>
                            </div>
                            <div className="p-4 space-y-2">
                                <h3 className="font-heading font-bold text-lg line-clamp-2">{e.title}</h3>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatDateTime(e.start_datetime, locale)}
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {e.venue?.name}
                                </div>
                                <Link to={`/organizer/events/${e.id}/manage`} data-testid={`manage-${e.id}`} className="btn-outline !py-2 !px-4 text-sm w-full text-center block">
                                    {t("organizer.manage_event")}
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
